import { useState, useRef } from 'react';
import { Upload, FileCheck2, Loader2 } from 'lucide-react';
import { onboardingApi } from '@/lib/api';

interface DocSlot {
  key:      string;
  label:    string;
  required: boolean;
  hint:     string;
}

const DOCS: DocSlot[] = [
  { key: 'mci',   label: 'MCI / State Medical Council Registration', required: true,  hint: 'Certificate issued by state medical council' },
  { key: 'cea',   label: 'Clinical Establishment Registration (CEA 2010)', required: true,  hint: 'Registration under Clinical Establishments Act' },
  { key: 'kyc',   label: 'Aadhaar + PAN of Proprietor / Director',   required: true,  hint: 'Both documents in a single PDF' },
  { key: 'msme',  label: 'MSME Certificate',                          required: false, hint: 'Optional — if registered as MSME' },
];

interface Props {
  tenantId: string;
  token:    string;
  onNext:   () => void;
  onBack:   () => void;
}

export default function Step2Documents({ tenantId, token, onNext, onBack }: Props) {
  const [files, setFiles] = useState<Record<string, File | null>>({
    mci: null, cea: null, kyc: null, msme: null,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]     = useState<string | null>(null);
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  function handleFile(key: string, file: File | null) {
    setFiles(prev => ({ ...prev, [key]: file }));
  }

  const requiredDone = DOCS.filter(d => d.required).every(d => files[d.key] !== null);

  async function handleSubmit() {
    if (!requiredDone) {
      setError('Please upload the 3 required documents before continuing.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const filenames = DOCS
        .filter(d => files[d.key])
        .map(d => files[d.key]!.name);
      await onboardingApi.step2(tenantId, token, filenames);
      onNext();
    } catch (e: any) {
      setError(e.response?.data?.error ?? 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="bg-orange/5 border border-orange/20 rounded-xl px-4 py-3 text-sm text-navy/70">
        Our team reviews documents within <strong className="text-navy">24 hours</strong>. You can
        continue to the next steps while verification is in progress.
      </div>

      <div className="space-y-3">
        {DOCS.map(doc => (
          <div key={doc.key}
            className="flex items-center gap-4 p-4 rounded-xl border border-gray-200 hover:border-orange/40 transition-colors">
            <div
              onClick={() => inputRefs.current[doc.key]?.click()}
              className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 cursor-pointer transition-colors
                ${files[doc.key]
                  ? 'bg-clinical-success/10 text-clinical-success'
                  : 'bg-gray-100 text-gray-400 hover:bg-orange/10 hover:text-orange'}`}>
              {files[doc.key] ? <FileCheck2 size={20}/> : <Upload size={20}/>}
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-navy">{doc.label}</span>
                {!doc.required && (
                  <span className="text-2xs bg-gray-100 text-gray-400 px-2 py-0.5 rounded-full">Optional</span>
                )}
                {doc.required && !files[doc.key] && (
                  <span className="text-2xs bg-red-50 text-clinical-danger px-2 py-0.5 rounded-full">Required</span>
                )}
              </div>
              <div className="text-xs text-gray-400 mt-0.5">
                {files[doc.key]
                  ? <span className="text-clinical-success font-medium">{files[doc.key]!.name}</span>
                  : doc.hint}
              </div>
            </div>

            <button type="button"
              onClick={() => inputRefs.current[doc.key]?.click()}
              className="text-xs text-orange hover:text-orange-dark font-medium flex-shrink-0">
              {files[doc.key] ? 'Replace' : 'Upload'}
            </button>

            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              className="hidden"
              ref={el => { inputRefs.current[doc.key] = el; }}
              onChange={e => handleFile(doc.key, e.target.files?.[0] ?? null)}
            />
          </div>
        ))}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-clinical-danger text-sm rounded-lg px-4 py-3">
          {error}
        </div>
      )}

      <div className="flex gap-3">
        <button type="button" onClick={onBack}
          className="flex-1 py-3 rounded-lg border border-gray-200 text-navy text-sm font-medium hover:bg-gray-50 transition-colors">
          Back
        </button>
        <button type="button" onClick={handleSubmit} disabled={submitting}
          className="flex-1 py-3 rounded-lg bg-orange text-navy font-semibold text-sm
                     hover:bg-orange-dark transition-colors disabled:opacity-50
                     flex items-center justify-center gap-2">
          {submitting && <Loader2 size={16} className="animate-spin"/>}
          Submit for Review
        </button>
      </div>
    </div>
  );
}
